/**
 * React Query mutation hooks for the requote resolution actions (ADR-031):
 * Replace, Disregard, and Restore. Each calls the contract-first BTX client
 * from `@platform/api`, and on success invalidates the RFP's proposal-list
 * queries so the SBS grid reflects the new state across every read scope.
 *
 * The invalidation targets `proposalQueryKeys.all(rfpId)` — the `["proposals",
 * rfpId]` prefix — which React Query matches against every scoped variant
 * (board / all / deliverable) the grid may have cached.
 */

import {
  proposalsDisregardProposalPlanGroupMutation,
  proposalsPromoteProposalPlanGroupMutation,
  proposalsReplaceProposalPlanGroupMutation,
  useApi,
  useHeyApi,
} from "@platform/api";
import { useNotification } from "@platform/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { restoreProposalPlanGroup } from "./clientProposalPlanGroups";
import { proposalQueryKeys } from "./useProposals";

import type { ProposalPlanGroup } from "../types/proposal.types";

/**
 * These mutations return a proposal-plan-group body that cannot pass the generated
 * zod validator: its timestamps carry RFC-3339 offsets that `z.string()
 * .datetime()` rejects, and the benefits union is self-contradictory — each
 * member requires `objectType` to equal both a PascalCase type-name literal
 * (e.g. `"RfpDentalBenefit"`) and its short enum value (`"dental"`) at once, so
 * no real payload validates. We discard the body anyway (onSuccess only
 * invalidates queries), so skip validation rather than throw on a response we
 * never read. The durable fix belongs in the OpenAPI specs (discriminator
 * mapping, timestamp offsets, nullability); until then this keeps the actions
 * from failing on a body they don't consume.
 */
const skipProposalDetailValidation = (): Promise<void> => Promise.resolve();

/**
 * Shared mutation key for all three requote actions, so a single
 * `useIsMutating` can show the grid's loading state while any of them is in
 * flight. Without it the grid stays idle for the whole action request and only
 * starts loading once the follow-up proposal refetch begins, which reads as
 * nothing having happened when the analyst confirms the dialog.
 */
export const REQUOTE_ACTION_MUTATION_KEY = ["requoteAction"] as const;

/** The result of a requote-action hook (Replace / Disregard / Restore). */
export interface RequoteActionResult {
  /**
   * Runs the action against the given plan group id and, on success, shows
   * `successMessage` as a toast. Resolves once the mutation settles; rejects if
   * it fails (no toast in that case). Scoped to the plan group (not the whole
   * proposal) so acting on one requested plan's quote never affects another
   * requested plan sharing the same document-ingested proposal (ADR-031,
   * CD-1792 follow-up).
   */
  act: (groupId: string, successMessage: string) => Promise<void>;
  isPending: boolean;
  error: Error | null;
}

/**
 * The result of the restore action, which differs from the others in needing the
 * server's answer before it can word its toast.
 */
export interface RestoreActionResult {
  /**
   * Restores the plan group and toasts the message `messageFor` builds from the restored
   * group. The message can't be fixed up front: restoring never promotes, so the quote
   * returns to the board or to Quote History depending on whether a Replace superseded it
   * while it sat deleted, and only the response says which.
   */
  act: (
    groupId: string,
    messageFor: (restored: ProposalPlanGroup) => string,
  ) => Promise<void>;
  isPending: boolean;
  error: Error | null;
}

/**
 * useRestoreProposalPlanGroup brings a deleted quote back out of Quote History (CD-89).
 *
 * Distinct from {@link usePromoteProposal}, which promotes a historical version over the
 * current active one. This only clears the deletion and leaves the version fields alone.
 *
 * Shares REQUOTE_ACTION_MUTATION_KEY so the grid shows its loading state for this action
 * too, rather than sitting idle until the follow-up refetch starts.
 */
export const useRestoreProposalPlanGroup = (
  rfpId: string,
): RestoreActionResult => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const mutation = useMutation({
    mutationKey: REQUOTE_ACTION_MUTATION_KEY,
    mutationFn: (groupId: string) =>
      restoreProposalPlanGroup(groupId, axiosInstance),
    onSuccess: () => {
      // Fire-and-forget, for the same reason as the actions below: awaiting the
      // invalidation would hold the caller open until the grid rebuild has torn down the
      // header that owns the dialog.
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
    },
  });

  return {
    act: async (groupId, messageFor) => {
      const restored = await mutation.mutateAsync(groupId);
      showSuccess(messageFor(restored));
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * useReplaceProposal promotes a pending requote to the active version of the
 * requested plan it answers (ADR-031 Replace): the requested plan's prior
 * active quote is demoted to Quote History and the requote becomes active.
 */
export const useReplaceProposal = (rfpId: string): RequoteActionResult => {
  const { btxClient } = useHeyApi();
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const mutation = useMutation({
    ...proposalsReplaceProposalPlanGroupMutation({
      client: btxClient,
      responseValidator: skipProposalDetailValidation,
    }),
    mutationKey: REQUOTE_ACTION_MUTATION_KEY,
    onSuccess: () => {
      // Fire-and-forget — do not return the invalidation promise. React Query
      // awaits a promise returned from onSuccess before mutateAsync settles;
      // returning it would delay the caller's post-await close until the refetch
      // has rebuilt the grid and torn down the header that owns the dialog,
      // leaving the modal orphaned and open. Refetch in the background instead.
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
    },
  });

  return {
    act: async (groupId, successMessage) => {
      await mutation.mutateAsync({ path: { id: groupId } });
      showSuccess(successMessage);
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * useDisregardProposal resolves a pending requote without promoting it — it
 * stays inactive and moves to Quote History, leaving the requested plan's
 * prior active quote in place (ADR-031 Disregard).
 */
export const useDisregardProposal = (rfpId: string): RequoteActionResult => {
  const { btxClient } = useHeyApi();
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const mutation = useMutation({
    ...proposalsDisregardProposalPlanGroupMutation({
      client: btxClient,
      responseValidator: skipProposalDetailValidation,
    }),
    mutationKey: REQUOTE_ACTION_MUTATION_KEY,
    onSuccess: () => {
      // Fire-and-forget — do not return the invalidation promise. React Query
      // awaits a promise returned from onSuccess before mutateAsync settles;
      // returning it would delay the caller's post-await close until the refetch
      // has rebuilt the grid and torn down the header that owns the dialog,
      // leaving the modal orphaned and open. Refetch in the background instead.
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
    },
  });

  return {
    act: async (groupId, successMessage) => {
      await mutation.mutateAsync({ path: { id: groupId } });
      showSuccess(successMessage);
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * usePromoteProposal re-promotes a historical (actioned, inactive) quote to the
 * active version of the requested plan it answers, demoting the requested
 * plan's current active quote (ADR-031).
 *
 * Not to be confused with {@link useRestoreProposalPlanGroup}, which undoes a deletion
 * and never promotes.
 */
export const usePromoteProposal = (rfpId: string): RequoteActionResult => {
  const { btxClient } = useHeyApi();
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const mutation = useMutation({
    ...proposalsPromoteProposalPlanGroupMutation({
      client: btxClient,
      responseValidator: skipProposalDetailValidation,
    }),
    mutationKey: REQUOTE_ACTION_MUTATION_KEY,
    onSuccess: () => {
      // Fire-and-forget — do not return the invalidation promise. React Query
      // awaits a promise returned from onSuccess before mutateAsync settles;
      // returning it would delay the caller's post-await close until the refetch
      // has rebuilt the grid and torn down the header that owns the dialog,
      // leaving the modal orphaned and open. Refetch in the background instead.
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
    },
  });

  return {
    act: async (groupId, successMessage) => {
      await mutation.mutateAsync({ path: { id: groupId } });
      showSuccess(successMessage);
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
